import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/src/shadComponents/ui/form';
import { Input } from '@/src/shadComponents/ui/input';
import { useState } from 'react';
import api from '@/src/services/api';
import { useNavigate } from 'react-router-dom';

const formSchema = z.object({
    email: z.string().email('Invalid email address'),
});

export default function PasswordResetRequest() {
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            await api.post('/password-reset/request', values);
            setSubmitted(true);
            setError(null);
        } catch (err) {
            setError('Failed to process request. Please try again.');
        }
    };

    if (submitted) {
        return (
            <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
                <Card className="w-[90%] md:min-w-2/6 md:w-2/6 rounded-none shadow-none border-0">
                    <CardHeader>
                        <CardTitle className="plex-sans-bold text-2xl">
                            Check Your Email
                        </CardTitle>
                        <CardDescription className="plex-sans text-lg">
                            We have received your request. If your email address
                            exists in our system, you will receive an email to
                            reset your password.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="plex-sans text-lg">
                            If you don't see an email, please check your spam
                            folder.
                        </p>
                        <br />
                        <p className="plex-sans text-lg">
                            If you continue to have issues, please contact
                            support@photoengineers.com.
                        </p>
                        <br />
                        <button
                            className="w-full black-button plex-sans-bold text-xl"
                            onClick={() => {
                                navigate('/login');
                            }}
                        >
                            Back to Login &rarr;
                        </button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
            <Card className="w-[90%] md:min-w-2/6 md:w-2/6 rounded-none shadow-none border-0">
                <CardHeader>
                    <CardTitle className="plex-sans-bold text-2xl">
                        Reset Password
                    </CardTitle>
                    <CardDescription className="plex-sans text-lg">
                        Enter your email address and we'll send you a link to
                        reset your password.
                        <br />
                        <br />
                        <b>Note:</b> Support will never ask you for your
                        password.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
                            {error}
                        </div>
                    )}
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-4"
                        >
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="plex-sans-bold text-xl">
                                            Email
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className="shadow-none monospace-font font-size-22 py-2"
                                                type="email"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <button
                                type="submit"
                                className="w-full black-button plex-sans-bold text-xl"
                            >
                                Reset Password
                            </button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
